import { Controller, useForm } from 'react-hook-form';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import { SelectDeviceOwner } from '#components/patterns/entity-select/device-owner/SelectDeviceOwner.tsx';
import { SelectRole } from '#components/patterns/entity-select/role/index.ts';
import { SelectTagsFilter } from '#components/patterns/entity-select/tag/SelectTagsFilter.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { Select } from '#components/patterns/forms/select/select/Select.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useHostFilters, type HOST_FILTERS } from '#hooks/useHostFilters.ts';
import { HOST_KIND_LABELS, HOST_KINDS, isHostKind } from '#util/host.ts';

type FilterFormValues = {
  [K in (typeof HOST_FILTERS)[number]]: K extends 'tags' ? string[] : boolean | string;
};

const DEFAULT_VALUES: FilterFormValues = {
  type: '',
  updateAvailable: false,
  isBlocked: false,
  unenrolled: false,
  mobile: false,
  networkID: '',
  roleID: '',
  endpointOIDCUserID: '',
  tags: [],
};

type Props = {
  onApply: () => void;
};

export function HostFilterForm({ onApply }: Props) {
  const { setParams, filters, tags } = useHostFilters();
  const { register, handleSubmit, control } = useForm<FilterFormValues>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...filters,
      tags,
    },
  });

  const onSubmit = handleSubmit((data) => {
    const newParams = Object.fromEntries(
      Object.entries(data).map(([filterName, filterValue]) => [
        `filter.${filterName}`,
        Array.isArray(filterValue) ?
          filterValue.length > 0 ?
            filterValue
          : undefined
        : filterValue === true ? 'true'
        : typeof filterValue === 'string' && filterValue !== '' ? filterValue
        : undefined,
      ])
    );
    // Clear cursor when changing filters, otherwise it will be invalid
    setParams({ ...newParams, cursor: undefined });
    onApply();
  });

  return (
    <form method="post" id="filter-form" onSubmit={onSubmit}>
      <Controller
        control={control}
        name="type"
        render={({ field }) => (
          <Select<string>
            label={<div className="label-sm mb-8">Filter by type</div>}
            className="width-100 mb-16"
            value={typeof field.value === 'string' ? field.value : ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            innerRef={field.ref}
            renderValue={(value) => (isHostKind(value) ? HOST_KIND_LABELS[value] : null)}
          >
            {/* The controlled value is resubmitted on every Apply, so without an explicit "all"
                item a chosen type could never be removed from this popover — only the dismissible
                pill could. The empty value maps to undefined on submit, like every unset field. */}
            <SelectItem value="">All types</SelectItem>
            {HOST_KINDS.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {HOST_KIND_LABELS[kind]}
              </SelectItem>
            ))}
          </Select>
        )}
      />
      <SelectDeviceOwner
        label={<div className="label-sm mb-8">Filter by device owner</div>}
        name="endpointOIDCUserID"
        control={control}
        placeholder="Please select…"
        className="width-100 mb-16"
        renderOptions={({ onChange, items }) => (
          <>
            {items.map((item) => (
              <SelectItem value={item.value} onClick={() => onChange(item.value)} searchable={true} key={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </>
        )}
      />
      <SelectRole
        label={<div className="label-sm mb-8">Filter by role</div>}
        name="roleID"
        control={control}
        placeholder="Please select…"
        className="width-100 mb-16"
        renderOptions={({ onChange, items }) => (
          <>
            {items.map((item) => (
              <SelectItem value={item.value} onClick={() => onChange(item.value)} searchable={true} key={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </>
        )}
      />
      <SelectTagsFilter
        label={<div className="label-sm mb-8">Filter by tags</div>}
        name="tags"
        control={control}
        className="width-100 mb-16"
      />
      <div className="label-sm mb-16">Other filters</div>
      <Spacer direction="vertical" size="narrow">
        <Checkbox {...register('updateAvailable')} label="Update available" />
        <Checkbox {...register('isBlocked')} label="Blocked" />
        <Checkbox {...register('unenrolled')} label="Not enrolled" />
        <Checkbox {...register('mobile')} label="Mobile" className="mb-16" />
        <Button type="submit" variant="secondary" size="small" className="width-100">
          Apply
        </Button>
      </Spacer>
    </form>
  );
}
